import { useState } from 'react';
import { ChevronsUpDown, LogOut, TriangleAlert } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuItem } from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/useAuth';

function toInitials(name: string): string {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return initials || '?';
}

export function NavUser() {
  const { user, logout } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const name = user?.name ?? 'Unknown user';
  const email = user?.email ?? '';
  const initials = toInitials(name);

  const AvatarWithDot = (
    <div className="relative shrink-0">
      <Avatar className="size-8 rounded-lg">
        <AvatarImage src={undefined} alt={name} />
        <AvatarFallback className="rounded-lg bg-primary/10 text-xs font-semibold text-primary">
          {initials}
        </AvatarFallback>
      </Avatar>
      <span className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-sidebar bg-emerald-500" />
    </div>
  );

  return (
    <SidebarMenu aria-label="user_menu">
      <SidebarMenuItem>
        {/* The whole card is a dropdown trigger — so Log out is reachable in
            both the expanded and the collapsed (icon-only) sidebar. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2.5 rounded-lg p-1.5 text-left outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring data-[state=open]:bg-sidebar-accent group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:hover:bg-transparent"
            >
              {AvatarWithDot}
              <div className="grid flex-1 leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate text-sm font-medium">{name}</span>
                <span className="truncate text-xs text-muted-foreground">{email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" sideOffset={8} className="w-[--radix-dropdown-menu-trigger-width] min-w-56">
            <DropdownMenuLabel className="flex items-center gap-2.5 p-2 font-normal">
              {AvatarWithDot}
              <div className="grid min-w-0 leading-tight">
                <span className="truncate text-sm font-medium">{name}</span>
                <span className="truncate text-xs text-muted-foreground">{email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setConfirmOpen(true)}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="size-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent className="sm:max-w-[400px]">
            <AlertDialogHeader>
              <div className="mb-1 flex size-11 items-center justify-center rounded-xl bg-destructive/10">
                <TriangleAlert className="size-5 text-destructive" />
              </div>
              <AlertDialogTitle>Log out of Oubo?</AlertDialogTitle>
              <AlertDialogDescription>
                You'll need to sign in again to access the admin dashboard.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-row gap-2.5">
              <AlertDialogCancel className="mt-0 flex-1">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => logout()}
                className="flex-1 bg-destructive text-white hover:bg-destructive/90"
              >
                Log out
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
